/** @fileoverview Configures authenticated requests for the Fabrik AI chat transport. */

import { DefaultChatTransport } from 'ai';
import { assistantMessagesPath } from '../services/chatApi';
import { request } from '../services/platformAuth';
import type { FabrikUIMessage } from '../types/chat';

export const createChatTransport = (tenantId: string, conversationId?: string) =>
  new DefaultChatTransport<FabrikUIMessage>({
    api: '/app/fabrik/assistant-conversations',
    fetch: (input, init) => {
      const inputUrl =
        typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const url = new URL(inputUrl, window.location.origin);
      const headers = Object.fromEntries(
        Array.from(new Headers(init?.headers)).filter(
          ([name]) => !['authorization', 'x-tenantid'].includes(name.toLowerCase())
        )
      );
      return request(`${url.pathname}${url.search}`, tenantId, { ...init, headers });
    },
    prepareSendMessagesRequest: ({ body }) => {
      const requestBody = body as Record<string, unknown> | undefined;
      const requestConversationId =
        typeof requestBody?.conversationId === 'string' ? requestBody.conversationId : undefined;
      if (!conversationId || requestConversationId !== conversationId) {
        throw new Error('Select a conversation before sending a message.');
      }
      if (Array.isArray(requestBody?.answers)) {
        return {
          api: assistantMessagesPath(conversationId),
          body: { answers: requestBody.answers },
        };
      }
      if (typeof requestBody?.question_id === 'string' && typeof requestBody.answer === 'string') {
        return {
          api: assistantMessagesPath(conversationId),
          body: {
            question_id: requestBody.question_id,
            answer: requestBody.answer,
          },
        };
      }
      if (typeof requestBody?.query !== 'string' || typeof requestBody.model_type !== 'string') {
        throw new Error('The assistant request is missing its query or model.');
      }
      return {
        api: assistantMessagesPath(conversationId),
        body: {
          query: requestBody.query,
          model_type: requestBody.model_type,
          ...(typeof requestBody.clientMessageId === 'string'
            ? { client_message_id: requestBody.clientMessageId }
            : {}),
          ...(typeof requestBody.app_id === 'string' ? { app_id: requestBody.app_id } : {}),
          ...(requestBody.plan_mode === true ? { plan_mode: true } : {}),
          ...(Array.isArray(requestBody.selection) ? { selection: requestBody.selection } : {}),
          ...(Array.isArray(requestBody.attachments)
            ? { attachments: requestBody.attachments }
            : {}),
        },
      };
    },
  });
